import { InferType, array, boolean, number, object, string } from "yup";
import fetchRest from "@/utils/fetchRest";

export interface WorkspaceBillingPricesInput {
  workspaceSlug: string;
  prices: Array<{ priceId: string; quantity: number }>;
}

const WorkspaceBillingChangePreviewOutputSchema = object({
  data: object({
    immediateTransaction: object({
      billingPeriod: object({
        startsAt: string().required(),
        endsAt: string().required(),
      }).required(),
      subtotalFormatted: string().required(),
      taxFormatted: string().required(),
      credit: string().required(),
      creditFormatted: string().required(),
      creditToBalance: string().required(),
      creditToBalanceFormatted: string().required(),
      grandTotalFormatted: string().required(),
    })
      .nullable()
      .optional(),
    deferred: boolean().optional(),
    nextBillDate: string().nullable().optional(),
    feedImpact: object({
      newFeedLimit: number().required(),
      currentFeedCount: number().required(),
      willBeDisabledCount: number().required(),
      affectedFeeds: array(
        object({
          id: string().required(),
          title: string().required(),
          url: string().required(),
          createdAt: string().required(),
        }).required(),
      ).optional(),
    }).optional(),
  }).required(),
}).required();

export type WorkspaceBillingChangePreviewOutput = InferType<
  typeof WorkspaceBillingChangePreviewOutputSchema
>;

export const previewWorkspaceBillingChange = async ({
  workspaceSlug,
  prices,
}: WorkspaceBillingPricesInput): Promise<WorkspaceBillingChangePreviewOutput> => {
  const res = await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/update-preview`, {
    validateSchema: WorkspaceBillingChangePreviewOutputSchema,
    requestOptions: {
      method: "POST",
      body: JSON.stringify({ prices }),
    },
  });

  return res as WorkspaceBillingChangePreviewOutput;
};

export const updateWorkspaceBilling = async ({
  workspaceSlug,
  prices,
}: WorkspaceBillingPricesInput): Promise<{
  deferred?: boolean;
  nextBillDate?: string | null;
} | void> => {
  const res = await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/update`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({ prices }),
    },
  });

  if (res instanceof Response) {
    return undefined;
  }

  if (res && typeof res === "object" && "data" in (res as Record<string, unknown>)) {
    return (res as { data: { deferred?: boolean; nextBillDate?: string | null } }).data;
  }

  return undefined;
};

export const cancelWorkspaceBilling = async (workspaceSlug: string): Promise<void> => {
  await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/cancel`, {
    requestOptions: {
      method: "POST",
    },
    skipJsonParse: true,
  });
};

export const resumeWorkspaceBilling = async (workspaceSlug: string): Promise<void> => {
  await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/resume`, {
    requestOptions: {
      method: "POST",
    },
    skipJsonParse: true,
  });
};

const WorkspaceUpdatePaymentMethodOutputSchema = object({
  data: object({
    paddleTransactionId: string().required(),
  }).required(),
}).required();

export type WorkspaceUpdatePaymentMethodOutput = InferType<
  typeof WorkspaceUpdatePaymentMethodOutputSchema
>;

export const getWorkspaceUpdatePaymentMethodTransaction = async (
  workspaceSlug: string,
): Promise<WorkspaceUpdatePaymentMethodOutput> => {
  const res = await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/update-payment-method`, {
    validateSchema: WorkspaceUpdatePaymentMethodOutputSchema,
    requestOptions: {
      method: "POST",
    },
  });

  return res as WorkspaceUpdatePaymentMethodOutput;
};

export const convertWorkspaceBilling = async ({
  workspaceSlug,
  feedIds,
}: {
  workspaceSlug: string;
  feedIds: string[];
}): Promise<void> => {
  await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/convert`, {
    requestOptions: {
      method: "POST",
      body: JSON.stringify({ feedIds }),
    },
    skipJsonParse: true,
  });
};

const WorkspaceBillingEmailOutputSchema = object({
  data: object({
    billingEmail: string().required(),
  }).required(),
}).required();

export type WorkspaceBillingEmailOutput = InferType<typeof WorkspaceBillingEmailOutputSchema>;

export const updateWorkspaceBillingEmail = async ({
  workspaceSlug,
  email,
}: {
  workspaceSlug: string;
  email: string;
}): Promise<WorkspaceBillingEmailOutput> => {
  const res = await fetchRest(`/api/v1/workspaces/${workspaceSlug}/billing/email`, {
    validateSchema: WorkspaceBillingEmailOutputSchema,
    requestOptions: {
      method: "PATCH",
      body: JSON.stringify({ email }),
    },
  });

  return res as WorkspaceBillingEmailOutput;
};
